'use client';

import { useState } from 'react';

import styled from '@emotion/styled';
import dialogClasses from '@mui/material/Dialog/dialogClasses';
import modalClasses from '@mui/material/Modal/modalClasses';

import { Modal } from '@web/ui/components/modal';

// A personal note from the founder on where Persona stands. Plain English on
// purpose: it is a signed letter, not interface copy, so it is not routed
// through the translation catalogue.

// Hidden until the announcement goes out on socials. Flip to true to bring the
// pill (and with it the letter) back; nothing else has to change.
export const SPIKE_MESSAGE_ENABLED = false;

const CONTACTS = [
  { label: 'Email', href: 'mailto:pierre.mattei2@gmail.com' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/pierre-mattei/' },
  { label: 'X', href: 'https://x.com/SpikePersona' },
];

const PARAGRAPHS = [
  'After two intense years pouring everything I had into Persona, circumstances surrounding our parent company have put the brand’s development on hold.',
  'Building Persona, and the community that grew around it, has been a personal dream.',
  'As sad as I am to see this adventure paused, I still believe we created something timeless. Out of respect for our holders, the team and everyone who followed the journey, I wanted the heart of the brand to stay open to anyone who wants to explore it. I will do my best to keep this site, the brand kit, the manga and everything around them online for as long as I can.',
  'As I write this, I don’t know what the future holds for Persona, or whether I will ever get the chance to build the IP up again. I will share more about what happened on my X account as soon as I legally can.',
  'In the meantime, I’m turning to my next professional adventure. If you’d like to reach out, you can find me here:',
];

const Trigger = styled.button`
  position: fixed;
  z-index: 20;
  left: ${({ theme }) => theme.spacing('1500')};
  bottom: ${({ theme }) => theme.spacing('1500')};
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing('0750')};
  padding: 6px 18px 6px 6px;
  border: 1px solid ${({ theme }) => theme.color.outlineVariant};
  border-radius: 999px;
  background: ${({ theme }) => theme.color.surfaceContainer};
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.bold};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  box-shadow: ${({ theme }) => theme.elevation.level3};
  cursor: pointer;
  transition:
    transform 160ms ease,
    box-shadow 160ms ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: ${({ theme }) => theme.elevation.level4};
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.onSurface};
    outline-offset: 2px;
  }

  ${({ theme }) => theme.breakpoints.down('sm')} {
    left: ${({ theme }) => theme.spacing('0750')};
    /* Netlify pins its own badge along the bottom edge on phones; sit above
       it rather than side by side, where the two pills collide. */
    bottom: 64px;
    padding-right: 14px;
    font-size: ${({ theme }) => theme.typescale.size['0750']};
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover {
      transform: none;
    }
  }
`;

const Avatar = styled.span`
  position: relative;
  width: 36px;
  height: 36px;
  flex: none;
  border-radius: 50%;
  background: #f2b52c url('/spike/message-avatar.webp') center / cover;

  /* A small unread dot, so the pill reads as a message and not a control. */
  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 9px;
    height: 9px;
    border-radius: 50%;
    background: #e2552b;
    box-shadow: 0 0 0 2px ${({ theme }) => theme.color.surfaceContainer};
  }
`;

const StyledModal = styled(Modal)`
  .${dialogClasses.paper} {
    padding: 0;
    gap: 0;
    overflow: hidden;
    width: 100%;
    max-width: 960px;
    display: grid;
    grid-template-columns: minmax(0, 5fr) minmax(0, 6fr);

    ${({ theme }) => theme.breakpoints.down('md')} {
      grid-template-columns: 1fr;
      overflow-y: auto;
    }
  }

  .${modalClasses.backdrop} {
    background-color: rgba(20, 16, 8, 0.72);
  }

  /* On a stacked layout the close button lands on the illustration, where a
     bare dark glyph disappears into the artwork. */
  .${dialogClasses.paper} > button {
    ${({ theme }) => theme.breakpoints.down('md')} {
      background: rgba(255, 255, 255, 0.88);
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.18);
    }
  }
`;

const Illustration = styled.div`
  background: #f2b52c url('/spike/message-illustration.webp') center / cover;
  min-height: 100%;

  ${({ theme }) => theme.breakpoints.down('md')} {
    min-height: 0;
    height: 240px;
    /* Keep the head and what spills out of it; the shirt can go. */
    background-position: 50% 30%;
  }
`;

const Letter = styled.article`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('1500')};
  max-height: min(78vh, 760px);
  overflow-y: auto;
  box-sizing: border-box;
  padding: ${({ theme }) => theme.spacing('3000')};

  ${({ theme }) => theme.breakpoints.down('md')} {
    max-height: none;
    overflow: visible;
    padding: ${({ theme }) => theme.spacing('2000')};
  }
`;

const Eyebrow = styled.span`
  color: ${({ theme }) => theme.color.onSurfaceVariant};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0750']};
  letter-spacing: 0.14em;
  text-transform: uppercase;
`;

const Salutation = styled.h2`
  margin: 0;
  /* Clear the modal's close button, which sits over this corner. */
  padding-right: 2rem;
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.brand.bold};
  font-size: ${({ theme }) => theme.typescale.size['5']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.condensed};
  letter-spacing: ${({ theme }) => theme.typescale.tracking.decreased};
  text-wrap: balance;

  ${({ theme }) => theme.breakpoints.down('sm')} {
    font-size: ${({ theme }) => theme.typescale.size['2']};
  }
`;

const Paragraph = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};
  opacity: ${({ theme }) => theme.opacity.dense};
`;

const Contacts = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing('0500')};
`;

const Contact = styled.a`
  display: inline-flex;
  align-items: center;
  padding: 8px 16px;
  border: 1px solid ${({ theme }) => theme.color.outlineVariant};
  border-radius: 999px;
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.mono.regular};
  font-size: ${({ theme }) => theme.typescale.size['0875']};
  text-decoration: none;
  transition: background 140ms ease;

  &:hover {
    background: ${({ theme }) => theme.color.onSurface};
    color: ${({ theme }) => theme.color.surfaceContainer};
  }
`;

const Signature = styled.p`
  margin: 0;
  padding-top: ${({ theme }) => theme.spacing('0750')};
  border-top: 1px solid ${({ theme }) => theme.color.outlineVariant};
  color: ${({ theme }) => theme.color.onSurface};
  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  line-height: ${({ theme }) => theme.typescale.lineHeight.extended};

  strong {
    display: block;
    font: ${({ theme }) => theme.typography.body.bold};
  }
`;

export const SpikeMessage = () => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <Trigger
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
      >
        <Avatar aria-hidden />
        A message from Spike
      </Trigger>

      <StyledModal
        open={open}
        onClose={() => setOpen(false)}
        maxWidth={false}
        aria-labelledby="spike-message-title"
      >
        <Illustration role="img" aria-label="Illustration of Spike" />
        <Letter>
          <Eyebrow>A message from Spike (Pierre Mattei)</Eyebrow>
          <Salutation id="spike-message-title">
            To my dear Persona community, and everyone who followed, loved and
            cherished Persona,
          </Salutation>

          {PARAGRAPHS.map((text) => (
            <Paragraph key={text}>{text}</Paragraph>
          ))}

          <Contacts>
            {CONTACTS.map(({ label, href }) => (
              <Contact
                key={label}
                href={href}
                target={href.startsWith('mailto:') ? undefined : '_blank'}
                rel="noreferrer"
              >
                {label}
              </Contact>
            ))}
          </Contacts>

          <Signature>
            Sincerely yours,
            <strong>Pierre Mattei (Spike)</strong>
          </Signature>
        </Letter>
      </StyledModal>
    </>
  );
};
